import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  ButtonToolbar,
  Modal,
  Schema,
  Loader,
  Container,
  Content,
} from "rsuite";
import { Form, Field } from "react-final-form";
import { StringType } from "schema-typed";
import { UserServices } from "../../../core/services/UserServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { HelperServices } from "../../../core/services/HelperServices";
import CustomSelector from "../../shared/customSelector";
import { isEmptyVal, isNotEmpty, isNotNull } from "../../../utils/helpers";
import { useNavigate } from "react-router-dom";
import {
  genderObjArr,
  httpStatusArr,
  messageTypeArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { get } from "lodash/fp";

const UpdateSettings = (props: any): any => {
  const { t } = useTranslation();
  const fromRef = useRef<any>();
  const navigate = useNavigate();
  const [loader, setLoading] = useState(false);
  const countryList = useSelector((state: any) => state.country.data);
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [areaList, setAreaList] = useState([]);

  const [patternError, setPatternError] = useState("");
  const [formData, setFormData] = useState<any>({
    default_country: "",
    default_state: "",
    default_city: "",
    default_area: "",
    default_customer_gender: "",
    setting_for: "location",
  });

  const [errorMessage, setErrorMessage] = useState("");
  const [cityNotFound, setCityNotFound] = useState(false);
  const [stateNotFound, setSateNotFound] = useState(false);
  const [areaNotFound, setAreaNotFound] = useState(false);

  useEffect(() => {
    init();
  }, []);

  async function init() {
    try {
      setLoading(true);
      const response: any = await UserServices.getUserSettings();
      const respData = get("data.data", response);
      let obj = { ...formData };
      const intFields = [
        "default_country",
        "default_state",
        "default_city",
        "default_area",
      ];
      Object.keys(obj).map((formKey) => {
        if (intFields.includes(formKey) && respData[formKey]) {
          obj[formKey] = parseInt(respData[formKey]);
        } else if (respData[formKey]) {
          obj[formKey] = respData[formKey];
        }
      });
      setFormData({ ...obj });
      preSelectFields(obj);
      setLoading(false);
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
      navigate(-1);
    }
  }

  function preSelectFields(obj: any) {
    if (obj.default_country) {
      handleCountryChange(obj.default_country);
    }
    if (obj.default_state) {
      handleStateChange(obj.default_state);
    }
    if (obj?.default_city && obj?.default_state && obj?.default_country) {
      handleCityChange(obj.default_city);
    }
    if (
      obj?.default_area &&
      obj?.default_city &&
      obj?.default_state &&
      obj?.default_country
    ) {
      handleAreaChange(obj.default_area);
    }
  }

  async function handleCountryChange(countryId: any, form?: any) {
    if (form) {
      form.change("default_country", countryId);
      form.change("default_state", "");
      form.change("default_city", "");
      form.change("default_area", "");
      setCityList([]);
      setAreaList([]);
    }

    try {
      const stateRes = await HelperServices.getAllValidStates({
        country: countryId,
      });
      const respData = get("data.data", stateRes);
      setStateList(respData);
      setSateNotFound(!respData.length);
    } catch (error) {
      console.log(error);
    }
  }

  async function handleStateChange(stateId: any, form?: any) {
    if (form) {
      form.change("default_state", stateId);
      form.change("default_city", "");
      form.change("default_area", "");
      setAreaList([]);
    }

    try {
      const res = await HelperServices.getAllValidCities({ state: stateId });
      const respData = get("data.data", res);
      setCityList(respData);
      setCityNotFound(!respData.length);
    } catch (error) {
      console.log(error);
    }
  }

  async function handleCityChange(cityId: any, form?: any) {
    if (form) {
      form.change("default_city", cityId);
      form.change("default_area", "");
    }

    try {
      const res = await HelperServices.getAllValidAreas({ city: cityId });
      const respData = get("data.data", res);
      setAreaList(respData);
      setAreaNotFound(!respData.length);
    } catch (error) {
      console.log(error);
    }
  }

  async function handleAreaChange(areaId: any, form?: any) {
    if (form) {
      form.change("default_area", areaId);
    }
  }

  function resetSelectionFor(resetFor: string, form?: any) {
    switch (resetFor) {
      case "country":
        form.change("default_country", "");
        form.change("default_state", "");
        form.change("default_city", "");
        form.change("default_area", "");
        setStateList([]);
        setCityList([]);
        setAreaList([]);
        break;
      case "state":
        form.change("default_state", "");
        form.change("default_city", "");
        form.change("default_area", "");
        setCityList([]);
        setAreaList([]);
        break;
      case "city":
        form.change("default_city", "");
        form.change("default_area", "");
        setAreaList([]);
        break;
      case "area":
        form.change("default_area", "");
        break;
    }
  }

  const onSubmit = (val: any) => {
    const form = new FormData();
    for (var key in formData) {
      if (isNotNull(val[key])) form.append(key, val[key]);
    }
    saveUserSetting(form);
  };

  async function saveUserSetting(form: any) {
    try {
      setLoading(true);
      let response: any;
      response = await UserServices.updateSettings(form);
      setLoading(false);
      UtilityServices.showToaster({
        message: response?.data?.message,
      });
      navigate(-1);
    } catch (error: any) {
      setLoading(false);
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
      setErrorMessage(error?.message);
    }
  }

  return (
    <div className="modal-container">
      {loader && (
        <Loader
          style={{ zIndex: 9999 }}
          size="lg"
          backdrop
          content={t("LOADING")}
          vertical
        />
      )}
      <Modal
        open={true}
        backdrop="static"
        onClose={(e: any) => {
          setPatternError("");
          setFormData({});
          navigate(-1);
        }}
        size="sm"
      >
        <Modal.Header className="mb-3">
          <Modal.Title>
            <b>
              {t("UPDATE")} {t("YOUR")} {t("SETTINGS")}
            </b>
            <br />
            <span style={{ fontSize: "12px" }}>
              ({t("CHANGE_SETTING_NOTE")})
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container>
            <Content className="content customer-create-wrap">
              <Form
                onSubmit={onSubmit}
                validate={(values: any) => {
                  const errors: any = {};
                  return errors;
                }}
                initialValues={{ ...formData }}
                render={({ handleSubmit, form }) => (
                  <form onSubmit={handleSubmit}>
                    <div className="form-part">
                      <div className="flex-container">
                        <Field name="default_country">
                          {({ input, meta }) => (
                            <div className="flex-item">
                              <CustomSelector
                                loaderMsg={t("FETCHING_DATA_OF", {
                                  field: t("COUNTRY").toLocaleLowerCase(),
                                })}
                                isRequired={false}
                                valueKey="id"
                                labelKey="country_name"
                                isDisable={!countryList?.length ? true : false}
                                labelName={t("COUNTRY")}
                                defaultValue={input.value}
                                onClear={(event: any) => {
                                  resetSelectionFor("country", form);
                                }}
                                listData={
                                  countryList?.length ? countryList : []
                                }
                                onSelect={(e: any, selectedObj: any) => {
                                  handleCountryChange(e, form);
                                }}
                              />
                              {meta.error && meta.touched && (
                                <span>{meta.error}</span>
                              )}
                            </div>
                          )}
                        </Field>
                        <Field name="default_state">
                          {({ input, meta }) => (
                            <div className="flex-item">
                              <CustomSelector
                                loaderMsg={
                                  form.getState().values.default_country &&
                                  !stateList?.length
                                    ? t("FETCHING_DATA_OF", {
                                        field: t("STATE").toLocaleLowerCase(),
                                      })
                                    : ""
                                }
                                valueKey="id"
                                labelKey="state_name"
                                isRequired={false}
                                isDisable={!stateList?.length ? true : false}
                                labelName={t("STATE")}
                                defaultValue={input.value}
                                onClear={(event: any) => {
                                  resetSelectionFor("state", form);
                                }}
                                listData={
                                  form.getState().values.default_country &&
                                  stateList?.length
                                    ? stateList
                                    : [{}]
                                }
                                onSelect={(e: any, selectedObj: any) => {
                                  handleStateChange(e, form);
                                }}
                              />
                              {meta.error && meta.touched && (
                                <span>{meta.error}</span>
                              )}
                              {meta.error &&
                                meta.touched &&
                                form.getState().values.default_country &&
                                !stateList?.length && <br />}
                              {form.getState().values.default_country &&
                                !stateList?.length &&
                                !stateNotFound && (
                                  <span>
                                    {t("FETCHING_DATA_OF", {
                                      field: t("STATE"),
                                    })}
                                  </span>
                                )}
                              {form.getState().values.default_country &&
                                !stateList?.length &&
                                stateNotFound && (
                                  <span>
                                    {t("NO_DATA_FOUND_OF", {
                                      field: t("STATE").toLocaleLowerCase(),
                                    })}
                                  </span>
                                )}
                            </div>
                          )}
                        </Field>
                        <Field name="default_city">
                          {({ input, meta }) => (
                            <div className="flex-item">
                              <CustomSelector
                                loaderMsg={
                                  form.getState().values.default_state &&
                                  !cityList?.length
                                    ? t("FETCHING_DATA_OF", {
                                        field: t("CITY"),
                                      })
                                    : ""
                                }
                                valueKey="id"
                                labelKey="city_name"
                                isRequired={false}
                                isDisable={!cityList?.length ? true : false}
                                labelName={t("CITY")}
                                defaultValue={input.value}
                                onClear={(event: any) => {
                                  resetSelectionFor("city", form);
                                }}
                                listData={
                                  form.getState().values.default_state &&
                                  cityList?.length
                                    ? cityList
                                    : [{}]
                                }
                                onSelect={(e: any, selectedObj: any) => {
                                  handleCityChange(e, form);
                                }}
                              />
                              {meta.error && meta.touched && (
                                <span>{meta.error}</span>
                              )}
                              {meta.error &&
                                meta.touched &&
                                form.getState().values.default_state &&
                                !cityList?.length && <br />}
                              {form.getState().values.default_state &&
                                !cityList?.length &&
                                !cityNotFound && (
                                  <span>
                                    {t("FETCHING_DATA_OF", {
                                      field: t("CITY").toLocaleLowerCase(),
                                    })}
                                  </span>
                                )}
                              {form.getState().values.default_state &&
                                cityNotFound && (
                                  <span>
                                    {t("NO_DATA_FOUND_OF", {
                                      field: t("CITY").toLocaleLowerCase(),
                                    })}
                                  </span>
                                )}
                            </div>
                          )}
                        </Field>
                        <Field name="default_area">
                          {({ input, meta }) => (
                            <div className="flex-item">
                              <CustomSelector
                                loaderMsg={
                                  form.getState().values.default_area &&
                                  !areaList?.length
                                    ? t("FETCHING_DATA_OF", {
                                        field: t("AREA"),
                                      })
                                    : ""
                                }
                                valueKey="id"
                                labelKey="area_name"
                                isRequired={false}
                                isDisable={!areaList?.length ? true : false}
                                labelName={t("AREA")}
                                defaultValue={input.value}
                                onClear={(event: any) => {
                                  resetSelectionFor("area", form);
                                }}
                                listData={
                                  form.getState().values.default_city &&
                                  areaList?.length
                                    ? areaList
                                    : [{}]
                                }
                                onSelect={(e: any, selectedObj: any) => {
                                  handleAreaChange(e, form);
                                }}
                              />
                              {meta.error && meta.touched && (
                                <span>{meta.error}</span>
                              )}
                              {meta.error &&
                                meta.touched &&
                                form.getState().values.default_city &&
                                !areaList?.length && <br />}
                              {form.getState().values.default_city &&
                                !areaList?.length &&
                                !areaNotFound && (
                                  <span>
                                    {t("FETCHING_DATA_OF", {
                                      field: t("AREA").toLocaleLowerCase(),
                                    })}
                                  </span>
                                )}
                              {form.getState().values.default_city &&
                                areaNotFound && (
                                  <span>
                                    {t("NO_DATA_FOUND_OF", {
                                      field: t("AREA").toLocaleLowerCase(),
                                    })}
                                  </span>
                                )}
                            </div>
                          )}
                        </Field>
                      </div>
                      <div className="flex-container">
                        <Field name="default_customer_gender">
                          {({ input, meta }) => (
                            <div className="flex-item">
                              <CustomSelector
                                valueKey="value"
                                labelKey="label"
                                isRequired={false}
                                labelName={
                                  t("DEFAULT") +
                                  " " +
                                  t("CUSTOMER") +
                                  " " +
                                  t("GENDER")
                                }
                                defaultValue={input.value}
                                onClear={(event: any) => {
                                  form.change("default_customer_gender", "");
                                }}
                                listData={genderObjArr}
                                onSelect={(e: any, selectedObj: any) => {
                                  form.change("default_customer_gender", e);
                                }}
                              />
                              {meta.error && meta.touched && (
                                <span>{meta.error}</span>
                              )}
                            </div>
                          )}
                        </Field>
                      </div>
                    </div>

                    <div className="create-action d-flex flex-end">
                      <ButtonToolbar
                        className="text-center"
                        style={{ width: "100%", justifyContent: "center" }}
                      >
                        <Button
                          appearance="primary"
                          type="submit"
                          loading={loader}
                        >
                          {t("UPDATE")}
                        </Button>
                        <Button
                          onClick={(e: any) => {
                            form.reset();
                            if (form.getState().values.default_country) {
                              handleCountryChange(
                                form.getState().values.default_country
                              );
                            }
                            if (form.getState().values.default_state) {
                              handleStateChange(
                                form.getState().values.default_state
                              );
                            }
                            if (form.getState().values.default_city) {
                              handleCityChange(
                                form.getState().values.default_city
                              );
                            }
                            navigate(-1);
                          }}
                          color="red"
                          appearance="primary"
                        >
                          {t("CANCEL")}
                        </Button>
                      </ButtonToolbar>
                    </div>
                  </form>
                )}
              />
            </Content>
          </Container>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default UpdateSettings;
